import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
Box,
Container,
Typography,
Card, useTheme,
CardContent,
Grid,
Button,
Avatar,
Divider,
IconButton,
Paper,
CircularProgress,
Alert,
Slide,
Grow,
Fade,
Chip,
Stack,
LinearProgress,
Tooltip,
Badge
} from '@mui/material';
import {
Visibility as VisibilityIcon,
CheckCircle as CheckCircleIcon,
Publish as PublishIcon,
Build as BuildIcon,
Folder as FolderIcon,
ShoppingBag as ShoppingBagIcon,
ArrowForward as ArrowForwardIcon,
Science as ScienceIcon,
School as SchoolIcon,
TrendingUp as TrendingUpIcon,
People as PeopleIcon,
Article as ArticleIcon,
Download as DownloadIcon,
Share as ShareIcon,
CalendarToday as CalendarIcon,
RemoveRedEye as ViewIcon,
BarChart as AnalyticsIcon,
OpenInNew as OpenInNewIcon,
Summarize as SummarizeIcon,
FormatQuote as FormatQuoteIcon,
MenuBook as MenuBookIcon,
Description as DescriptionIcon,
Assignment as AssignmentIcon,
AccountTree as AccountTreeIcon,
LibraryBooks as LibraryBooksIcon,
Calculate as CalculateIcon,
Psychology as PsychologyIcon,
CheckBox as CheckBoxIcon,
Tune as TuneIcon,
NoteAdd as NoteAddIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';
import { getIdFullName, convertToUpperCase, daysPassed, getIdCategory, manageUserFilters } from '../../utilities/Functions';
import { APICategoriesList, APIResearcherList, APIResearchList, APIUniversitiesList, APIProductList,
     APIProjectList, APIResearchViewsList, APIPublishedWorkList } from '../../utilities/APIS';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../utilities/Text';
import GuestUser from '../main/GuestUser';
import LoadingState from '../../components/LoadingState';

const StyledCard = styled(Card)(({ theme }) => ({
transition: 'transform 0.3s, box-shadow 0.3s',
borderRadius: '16px',
border: `1px solid ${theme.palette.divider}`,
'&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[16],
    cursor: 'pointer'
}
}));

const StatCard = styled(Card)(({ theme, color }) => ({
borderLeft: `6px solid ${theme.palette[color].main}`,
height: '100%',
transition: 'transform 0.3s',
borderRadius: '12px',
background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
'&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[8]
}
}));

const Dashboard = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const showToast = useCustomToast();
    const [userData, setUserData] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        AreasData: [],
        ProjectsData: [],
        ProductsData: [],
        ResearchData: [],
        CompletedData: [],
        PublishedData: [],
        WorkingPublishedData: [],
        ResearchersData: [],
        ResearchViewsData: [],
        UniversitiesData: [],
        PublicationsData: []
    });

    const [loading, setLoading] = useState({
        initial: true,
        data: false
    });

    const [error, setError] = useState(null);

    useEffect(() => {
        loadUser();
        fetchData();
    }, []);

useEffect(() => {
    if (userData?.id) {
        loadUserData();
    }
}, [userData]);

const loadUser = () => {
    try {
    const user = sessionStorage.getItem('reseacher');
    if (user) {
        const jsonData = JSON.parse(user);
        setUserData(jsonData);

        // Create researcher object with proper structure
        const researcher = {
        name: jsonData.full_name || jsonData.name,
        profileImage: jsonData.profile_image,
        linkedin: jsonData.linkedin,
        instagram: jsonData.instagram,
        facebook: jsonData.facebook,
        twitter: jsonData.twitter,
        level: jsonData.level,
        about: jsonData.about,
        nationality: jsonData.nationality,
        email: jsonData.email,
        contact: jsonData.contact,
        title: jsonData.title,
        university: jsonData.university,
        id: jsonData.id,
        loggedIn: true
        };

        // Update session storage with standardized structure
        sessionStorage.setItem('reseacher', JSON.stringify(researcher));
        setUserData(researcher);
        
        showToast("Welcome Back", `Hello ${researcher.name}!`, "success");
    }
    } catch (error) {
    console.error('Error loading user:', error);
    showToast("Session Error", "Please log in again", "error");
    }
};

const fetchData = async () => {
    setLoading(prev => ({ ...prev, initial: true }));
    
    try {
        const [reseRes, uniRes, catsRes, researchRes, productsRes, projectsRes, viewsRes, publicationsRes] = await Promise.all([
            axios.get(APIResearcherList, { headers }),
            axios.get(APIUniversitiesList, { headers }),
            axios.get(APICategoriesList, { headers }),
            axios.get(APIResearchList, { headers }),
            axios.get(APIProductList, { headers }),
            axios.get(APIProjectList, { headers }),
            axios.get(APIResearchViewsList, { headers }),
            axios.get(APIPublishedWorkList, { headers })
        ]);

        const researchers = Array.isArray(reseRes.data.data) ? reseRes.data.data : [];
        const universities = Array.isArray(uniRes.data.data) ? uniRes.data.data : [];
        const categories = Array.isArray(catsRes.data.data) ? catsRes.data.data : [];
        const research = Array.isArray(researchRes.data.data) ? researchRes.data.data : [];
        const products = Array.isArray(productsRes.data.data) ? productsRes.data.data : [];
        const projects = Array.isArray(projectsRes.data.data) ? projectsRes.data.data : [];
        const views = Array.isArray(viewsRes.data.data) ? viewsRes.data.data : [];
        const publications = Array.isArray(publicationsRes.data.data) ? publicationsRes.data.data : [];
        
        console.log("All Publications from API:", JSON.stringify(publicationsRes.data.data));
        console.log("Total Publications Count:", publications.length);

        setDashboardData(prev => ({
            ...prev,
            AreasData: categories,
            ResearchersData: researchers,
            UniversitiesData: universities,
            ResearchData: research,
            ProductsData: products,
            ProjectsData: projects,
            ResearchViewsData: views,
            PublicationsData: publications,
        }));

        showToast("Success", TEXT_DATA_LOADED, "success");
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || TEXT_CONNECTION_ERROR;
        setError(errorMessage);
        showToast("Error", errorMessage, "error");
    } finally {
        setLoading(prev => ({ ...prev, initial: false }));
    }
};

// Update the loadUserData function in your Dashboard.js

const loadUserData = async () => {
    if (!userData?.id) return;
    setLoading(prev => ({ ...prev, data: true }));

    try {
        const userId = userData.id;
        
        console.log("Current User ID:", userId);
        console.log("Publications Data:", dashboardData.PublicationsData);

        // Filter data based on researcherName (userId)
        const userResearch = dashboardData.ResearchData.filter(item => 
            item.researcherName?.toString() === userId.toString()
        );
        
        const userProducts = dashboardData.ProductsData.filter(item => 
            item.researcherName?.toString() === userId.toString()
        );
        
        const userProjects = dashboardData.ProjectsData.filter(item => 
            item.researcherName?.toString() === userId.toString()
        );

        const researchViews = dashboardData.ResearchViewsData.filter(item => 
            item.researcherName?.toString() === userId.toString()
        );


        // Filter research by status
        const filteredPublishedData = userResearch.filter(item => item.researchStatus === "Published");
        const filteredCompletedData = userResearch.filter(item => item.researchStatus === "Completed");
        const filteredWorkingPublishedData = userResearch.filter(item => item.researchStatus === "Work In Progress");

        setDashboardData(prev => ({
            ...prev,
            PublishedData: filteredPublishedData,
            CompletedData: filteredCompletedData,
            WorkingPublishedData: filteredWorkingPublishedData,
            ProductsData: userProducts,
            ProjectsData: userProjects,
            ResearchViewsData: researchViews,
        }));

        showToast("Data Loaded", "Your dashboard data has been updated", "success");
    } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Failed to load user data";
        setError(errorMessage);
        showToast("Error", errorMessage, "error");
    } finally {
        setLoading(prev => ({ ...prev, data: false }));
    }
};

    // Updated goToResearchDetails function with all new fields
    const goToResearchDetails = async (research) => {
        const detailsProps = {
            // Basic Information
            id: research.id,
            researchTopic: research.researchTopic,
            researcherName: research.researcherName,
            researchArea: research.researchArea,
            researchStatus: research.researchStatus,
            researchType: research.researchType,
            fileName: research.fileName,
            fileSize: research.fileSize,
            uploadDate: research.uploadDate,
            universityName: research.universityName,
            pdfPlainText: research.pdfPlainText,
            
            // New Fields from Migration
            abstract: research.abstract,
            acronyms: research.acronyms,
            definition_of_terms: research.definition_of_terms,
            introduction: research.introduction,
            objectives: research.objectives,
            hypothesis: research.hypothesis,
            justification: research.justification,
            significance: research.significance,
            theoretical_framework: research.theoretical_framework,
            literature_review: research.literature_review,
            methodology: research.methodology,
            results_findings: research.results_findings,
            discussion_of_results: research.discussion_of_results,
            conclusions_recommendations: research.conclusions_recommendations,
            references: research.references,
            appendices: research.appendices,
            
            // JSON fields (for backward compatibility)
            researchAbstract: research.researchAbstract ? JSON.parse(research.researchAbstract) : [],
            researchMainObjectives: research.researchMainObjectives ? JSON.parse(research.researchMainObjectives) : [],
            researchSpecificObjectives: research.researchSpecificObjectives ? JSON.parse(research.researchSpecificObjectives) : [],
            researchHypothesis: research.researchHypothesis ? JSON.parse(research.researchHypothesis) : [],
            researchTheoreticalFrame: research.researchTheoreticalFrame ? JSON.parse(research.researchTheoreticalFrame) : [],
            researchLiteratureReview: research.researchLiteratureReview ? JSON.parse(research.researchLiteratureReview) : [],
            researchMethodology: research.researchMethodology ? JSON.parse(research.researchMethodology) : [],
            researchResults: research.researchResults ? JSON.parse(research.researchResults) : [],
            researchDiscussionOfResults: research.researchDiscussionOfResults ? JSON.parse(research.researchDiscussionOfResults) : [],
            researchRecommendations: research.researchRecommendations ? JSON.parse(research.researchRecommendations) : [],
            researchConclusions: research.researchConclusions ? JSON.parse(research.researchConclusions) : [],
        };

        navigate("/research-details", { state: detailsProps });
    };

    const goToProductsDetails = (product) => {
        const detailsProps = {
        productTitle: product.productTitle,
        researcherName: product.researcherName,
        uploadDate: product.uploadDate,
        productEnd: product.productEnd,
        productStart: product.productStart,
        universityName: product.universityName,
        productDetails:product.productDetails,
        productDescription: product.productDescription,
        };
        navigate("/product-details", { state: detailsProps });
    };
    
    const goToProjectDetails = (project) => {
        const detailsProps = {
        projectTitle: project.projectTitle,
        researcherName: project.researcherName,
        uploadDate: project.uploadDate,
        projectEnd: project.projectEnd,
        projectStart: project.projectStart,
        universityName: project.universityName,
        projectDescription: project.projectDescription,
        projectFunders: project.projectFunders,
        projectMembers: project.projectMembers,
        };
        navigate("/project-details", { state: detailsProps });
    };

    const goToResearchViewDetails = (researchView) => {
        // Navigate to research details with the viewed research data
        const detailsProps = {
            id: researchView.id,
            researchTopic: researchView.researchTopic,
            researcherName: researchView.researcherName,
            researchArea: researchView.researchArea,
            researchStatus: researchView.researchStatus,
            researchType: researchView.researchType,
            fileName: researchView.fileName,
            uploadDate: researchView.uploadDate,
            universityName: researchView.universityName,
            abstract: researchView.abstract,
            objectives: researchView.objectives,
            methodology: researchView.methodology,
            results_findings: researchView.results_findings,
            conclusions_recommendations: researchView.conclusions_recommendations,
            references: researchView.references,
            // Add view-specific information
            viewDate: researchView.viewDate,
            viewedBy: researchView.researchViewer
        };
        navigate("/research-details", { state: detailsProps });
    };

    const goToPublications = () => {
        navigate("/my-publications");
    };

    const goToAddPublication = () => {
        navigate("/submit-publication");
    };

        // Safe data filtering functions
    const getUserFilteredData = (data) => {
        if (!userData?.id) return [];
        return data.filter(item =>
            item.researcherName?.toString() === userData.id.toString()
        );
    };

    const userFilteredResearch = getUserFilteredData(dashboardData.ResearchData);
    const userFilteredProducts = getUserFilteredData(dashboardData.ProductsData);
    const userFilteredProjects = getUserFilteredData(dashboardData.ProjectsData);
    const userFilteredResearchViews = getUserFilteredData(dashboardData.ResearchViewsData);
    const userFilteredPublishedData = getUserFilteredData(dashboardData.PublishedData);
    const userFilteredCompletedData = getUserFilteredData(dashboardData.CompletedData);
    const userFilteredWorkingPublishedData = getUserFilteredData(dashboardData.WorkingPublishedData);
    const userFilteredPublications = dashboardData.PublicationsData.filter(item =>
    item.author_id?.toString() === userData?.id?.toString()
);
    const goToProjects = (userFilteredProjects) => {
        if (userFilteredProjects && userFilteredProjects.length > 0) {
            manageUserFilters(true, userData.id);
            navigate("/projects", {
                state: { userOnly: true, userId: userData.id} });
        } else {
            showToast("No Projects", "You don't have any projects yet", "info");
            navigate("/project-submit");
        }
    };

    const goToProducts = (userFilteredProducts) => {
        if (userFilteredProducts && userFilteredProducts.length > 0) {
            manageUserFilters(true, userData.id);
            navigate("/products", {
                state: { userOnly: true, userId: userData.id} });
        } else {
            showToast("No Products", "You don't have any products yet", "info");
            navigate("/product-submit");
        }
    };

    const goToResearch = (userFilteredResearch) => {
        if (userFilteredResearch && userFilteredResearch.length > 0) {
            manageUserFilters(true, userData.id);
            navigate("/researches", {
                state: { userOnly: true, userId: userData.id} });
        } else {
            showToast("No Research", "You don't have any research yet", "info");
            navigate("/research-submit");
        }
    };

    const goToResearchViews = (userFilteredResearchViews) => {
        if (userFilteredResearchViews && userFilteredResearchViews.length > 0) {
            navigate("/research-views", {
                state: { researchViews: userFilteredResearchViews }
            });
        } else {
            showToast("No Views", "You don't have any research views yet", "info");
        }
    };
    
const renderStatCard = (title, value, icon, color, description) => (
    <Grow in={true}>
    <StatCard color={color} sx={{ cursor: 'pointer' }}>
        <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.contrastText`, width: 56, height: 56 }}>
                {icon}
            </Avatar>
            <Chip
                label={value}
                color={color}
                size="small"
                sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
            />
            </Box>
            <Box>
            <Typography fontWeight="bold" color="text.primary">
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {description}
            </Typography>
            </Box>
        </Stack>
        </CardContent>
    </StatCard>
    </Grow>
);

// Updated renderResearchCard to show more information from new fields
const renderResearchCard = (research, index) => (
    <Slide direction="up" in={true} timeout={(index + 1) * 150}>
    <StyledCard sx={{ mb: 2 }}>
        <CardContent>
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Chip 
                icon={<ScienceIcon />}
                label={research.researchStatus}
                color={
                    research.researchStatus === 'Completed' ? 'success' :
                    research.researchStatus === 'Published' ? 'primary' : 'warning'
                }
                size="small"
                />
                <Typography variant="h6" sx={{ mt: 1 }} color="primary">
                {research.researchTopic}
                </Typography>
            </Box>
            <Button 
                size="small"
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => goToResearchDetails(research)}
            >
                View Details
            </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
                icon={<SchoolIcon />}
                label={getIdCategory(research.researchArea, dashboardData.AreasData)}
                size="small"
                variant="outlined"
            />
            <Chip
                icon={<CalendarIcon />}
                label={research.uploadDate}
                size="small"
                variant="outlined"
            />
            <Chip
                label={research.researchType}
                size="small"
                color="secondary"
            />
            </Box>

            {/* Show abstract preview if available */}
            {research.abstract && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        <SummarizeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Abstract Preview:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.5
                    }}>
                        {research.abstract.substring(0, 150)}...
                    </Typography>
                </Box>
            )}
        </Stack>
        </CardContent>
    </StyledCard>
    </Slide>
);

const renderPublicationCard = (publication, index) => {
    // Safety check - if publication is undefined or null, return null
    if (!publication) return null;
    
    // Get the publication type icon
    const getPubIcon = () => {
        switch(publication.publication_type) {
            case 'journal': return <MenuBookIcon />;
            case 'conference': return <ScienceIcon />;
            case 'book': return <LibraryBooksIcon />;
            case 'chapter': return <ArticleIcon />;
            case 'thesis': return <SchoolIcon />;
            default: return <ArticleIcon />;
        }
    };

    return (
        <Slide direction="up" in={true} timeout={(index + 1) * 150}>
            <StyledCard sx={{ mb: 2 }}>
                <CardContent>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Chip 
                                    icon={getPubIcon()}
                                    label={publication.publication_type?.charAt(0).toUpperCase() + publication.publication_type?.slice(1) || 'Journal'}
                                    color={
                                        publication.publication_type === 'journal' ? 'primary' :
                                        publication.publication_type === 'book' ? 'secondary' : 'default'
                                    }
                                    size="small"
                                />
                                <Typography variant="h6" sx={{ mt: 1 }} color="primary">
                                    {publication.title || 'Untitled'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {publication.author_name || 'Unknown Author'}
                                </Typography>
                            </Box>
                            {/* <Button 
                                size="small"
                                variant="outlined"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => goToPublications()}
                            >
                                View Details
                            </Button> */}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                                icon={<CalendarIcon />}
                                label={`Year: ${publication.publication_year || 'N/A'}`}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={publication.publisher || 'No Publisher'}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={publication.status || 'unknown'}
                                size="small"
                                color={publication.status === 'published' ? 'success' : 'warning'}
                            />
                        </Box>

                        {/* Show abstract preview if available */}
                        {publication.abstract && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    <SummarizeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                    Abstract Preview:
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mt: 0.5
                                }}>
                                    {publication.abstract.substring(0, 150)}...
                                </Typography>
                            </Box>
                        )}

                        {/* Show DOI if available */}
                        {publication.doi && (
                            <Typography variant="caption" color="primary">
                                DOI: {publication.doi}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </StyledCard>
        </Slide>
    );
};

const renderProductCard = (product, index) => (
    <Slide direction="up" in={true} timeout={(index + 1) * 150}>
    <StyledCard sx={{ mb: 2 }}>
        <CardContent>
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Chip 
                icon={<ShoppingBagIcon />}
                label="Product"
                color="info"
                size="small"
                />
                <Typography variant="h6" sx={{ mt: 1 }} color="primary">
                {product.productTitle}
                </Typography>
            </Box>
            <Button 
                size="small" 
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => goToProductsDetails(product)}
            >
                View Details
            </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
            }}>
            {product.productDescription}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
                icon={<CalendarIcon />}
                label={product.uploadDate}
                size="small"
                variant="outlined"
            />
            <Chip
                label={`${daysPassed(product.uploadDate)} days ago`}
                size="small"
                color="primary"
            />
            </Box>
        </Stack>
        </CardContent>
    </StyledCard>
    </Slide>
);

const renderProjectCard = (project, index) => (
    <Slide direction="up" in={true} timeout={(index + 1) * 150}>
    <StyledCard sx={{ mb: 2 }}>
        <CardContent>
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Chip 
                icon={<FolderIcon />}
                label="Project"
                color="secondary"
                size="small"
                />
                <Typography variant="h6" sx={{ mt: 1 }} color="primary">
                {project.projectTitle}
                </Typography>
            </Box>
            <Button 
                size="small" 
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => goToProjectDetails(project)}
            >
                View Details
            </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
            }}>
            {project.projectDescription}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
                icon={<CalendarIcon />}
                label={`${project.projectStart} - ${project.projectEnd}`}
                size="small"
                variant="outlined"
            />
            <Chip
                label={`${JSON.parse(project.projectMembers || '[]').length} members`}
                size="small"
                color="success"
            />
            </Box>
        </Stack>
        </CardContent>
    </StyledCard>
    </Slide>
);

// Updated renderResearchViewCard to include new fields
const renderResearchViewCard = (researchView, index) => (
    <Slide direction="up" in={true} timeout={(index + 1) * 150}>
    <StyledCard sx={{ mb: 2 }}>
        <CardContent>
        <Stack spacing={2}>
            {/* Header with Analytics Icon and Status */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Chip 
                icon={<AnalyticsIcon />}
                label="Research View"
                color="success"
                size="small"
                />
                <Typography variant="h6" sx={{ mt: 1 }} color="primary">
                {researchView.researchTopic}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Viewed by Researcher #{researchView.researchViewer}
                </Typography>
            </Box>
            <Button 
                size="small" 
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                onClick={() => goToResearchViewDetails(researchView)}
            >
                View Research
            </Button>
            </Box>

            {/* Research Information */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
                icon={<SchoolIcon />}
                label={getIdCategory(researchView.researchArea, dashboardData.AreasData)}
                size="small"
                variant="outlined"
                color="primary"
            />
            <Chip
                icon={<ViewIcon />}
                label={`Viewed on ${researchView.viewDate}`}
                size="small"
                variant="outlined"
                color="info"
            />
            <Chip
                icon={<TrendingUpIcon />}
                label={researchView.researchStatus}
                size="small"
                color={
                    researchView.researchStatus === 'Completed' ? 'success' :
                    researchView.researchStatus === 'Published' ? 'primary' : 'warning'
                }
            />
            </Box>

            {/* Abstract Preview if available */}
            {researchView.abstract && (
                <Box sx={{ 
                    backgroundColor: 'grey.50', 
                    p: 1.5, 
                    borderRadius: 1,
                    mt: 1
                }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SummarizeIcon sx={{ fontSize: 14 }} />
                        Abstract Preview:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.5
                    }}>
                        {researchView.abstract?.substring(0, 120)}...
                    </Typography>
                </Box>
            )}

            {/* Additional Information */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                backgroundColor: 'grey.50',
                p: 1,
                borderRadius: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="action" sx={{ fontSize: 18 }} />
                    <Typography variant="caption" color="text.secondary">
                        Uploaded: {researchView.uploadDate}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon color="action" sx={{ fontSize: 18 }} />
                    <Typography variant="caption" color="text.secondary">
                        {daysPassed(researchView.viewDate)} days since view
                    </Typography>
                </Box>
            </Box>

            {/* Quick Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="View Research Details">
                    <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => goToResearchViewDetails(researchView)}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Download Research">
                    <IconButton size="small" color="secondary">
                        <DownloadIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Share Research">
                    <IconButton size="small" color="info">
                        <ShareIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Stack>
        </CardContent>
    </StyledCard>
    </Slide>
);


    // Show GuestUser if user is not logged in
    if (!userData?.id) {
        return (<>
            <NavigationMenu />
            <br></br><br></br>
            <GuestUser />
        </>)
    }

if (loading.initial) {
    return (
        <>
            <NavigationMenu />
            <LoadingState/>
        </>
    );
}

return (
    <>
    <NavigationMenu />
    <Container maxWidth="lg" sx={{ mt: 8, py: 4 }}>
        {/* Header Section */}
        <Fade in={true}>
        <Paper elevation={3} sx={{
            p: 4, 
            mb: 4, 
            borderRadius: 3,
            background: theme.palette.success.main,
            color: 'white'
        }}>
            <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
                <Typography variant="h5" component="h5" gutterBottom fontWeight="bold">
                Research and Innovations Academy
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Welcome back, {userData.title} {userData.name}!
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.8, mt: 1 }}>
                {getIdCategory (userData.university, dashboardData.UniversitiesData)}
                </Typography>
            </Grid>
            </Grid>
        </Paper>
        </Fade>

        {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
        </Alert>
        )}

        {/* Statistics Grid */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
            {renderStatCard(
                'Completed',
                userFilteredCompletedData.length,
                <CheckCircleIcon />,
                'success',
                'Finished research',
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
            {renderStatCard(
                'In Progress',
                userFilteredWorkingPublishedData.length,
                <BuildIcon />,
                'warning',
                'Ongoing research',
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
            {renderStatCard(
                'Products',
                userFilteredProducts.length,
                <ShoppingBagIcon />,
                'info',
                'Research products',
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
            {renderStatCard(
                'Projects',
                userFilteredProjects.length,
                <FolderIcon />,
                'secondary',
                'Research projects',
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
            {renderStatCard(
                'Publications',
                userFilteredPublications.length,
                <ArticleIcon />,
                'primary',
                'Academic publications',
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
            {renderStatCard(
                'Research Views',
                userFilteredResearchViews.length,
                <ViewIcon />,
                'info',
                'Times research viewed',
            )}
        </Grid>
        </Grid>

        {/* Main Content Grid */}
        <Box>
            {/* Publications Section */}
            <Grid item xs={12} sx={{ mb: 4 }}>
                <Fade in={true}>
                    <Card sx={{ 
                        borderRadius: 3,
                        flex: 1,
                        minHeight: { xs: 200, md: 240 }
                    }}>
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h5" color="primary" fontWeight="bold">
                                    <ArticleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Recent Publications
                                </Typography>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={()=>goToPublications()}
                                    size="small"
                                    disabled={userFilteredPublications.length === 0}
                                >
                                    View All {userFilteredPublications.length > 0 && `(${userFilteredPublications.length})`}
                                </Button>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ flex: 1 }}>
                                {userFilteredPublications.slice(0, 3)
                                    .map((publication, index) => renderPublicationCard(publication, index))
                                }

                                {userFilteredPublications.length === 0 && (
                                    <Box sx={{
                                        textAlign: 'center',
                                        py: 4,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>
                                        <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No Publications Yet
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Share your published academic works
                                        </Typography>
                                        <Button variant="contained" onClick={goToAddPublication}>
                                            Add Publication
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Fade>
            </Grid>

            {/* Recent Research and Research Views Row */}
            <Grid container spacing={3}>
                {/* Recent Research */}
                <Grid item xs={12} md={6}>
                    <Fade in={true}>
                        <Card sx={{ 
                            borderRadius: 3,
                            flex: 1,
                            minHeight: { xs: 200, md: 240 }
                        }}>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h5" color="primary" fontWeight="bold">
                                        Recent Research Work
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={()=>goToResearch(userFilteredResearch)}
                                        size="small"
                                        disabled={userFilteredResearch.length === 0}
                                    >
                                        View All {userFilteredResearch.length > 0 && `(${userFilteredResearch.length})`}
                                    </Button>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <Box sx={{ flex: 1 }}>
                                    {userFilteredResearch.slice(0, 3)
                                        .map((research, index) => renderResearchCard(research, index))
                                    }

                                    {userFilteredResearch.length === 0 && (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <ScienceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No Research Yet
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Start by uploading your first research work
                                            </Typography>
                                            <Button variant="contained" onClick={() => navigate('/research-submit')}>
                                                Submit Research
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Fade>
                </Grid>

                {/* Research Views Section */}
                <Grid item xs={12} md={6}>
                    <Fade in={true}>
                        <Card sx={{ 
                            borderRadius: 3,
                            flex: 1,
                            minHeight: { xs: 200, md: 240 }
                        }}>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h5" color="primary" fontWeight="bold">
                                        Research Views
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        endIcon={<AnalyticsIcon />}
                                        onClick={()=>goToResearchViews(userFilteredResearchViews)}
                                        size="small"
                                        disabled={userFilteredResearchViews.length === 0}
                                    >
                                        View All {userFilteredResearchViews.length > 0 && `(${userFilteredResearchViews.length})`}
                                    </Button>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <Box sx={{ flex: 1 }}>
                                    {userFilteredResearchViews.slice(0, 3)
                                        .map((researchView, index) => renderResearchViewCard(researchView, index))
                                    }

                                    {userFilteredResearchViews.length === 0 && (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <ViewIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No Research Views Yet
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Your research hasn't been viewed by others yet
                                            </Typography>
                                            <Button variant="contained" onClick={() => navigate('/researches')}>
                                                Share Your Research
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Fade>
                </Grid>
            </Grid>

            {/* Recent Products & Projects Row */}
            <br />
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Stack spacing={4} sx={{ height: '100%' }}>
                        {/* Products Section */}
                        <Fade in={true}>
                            <Card sx={{ 
                                borderRadius: 3,
                                flex: 1,
                                minHeight: { xs: 200, md: 240 }
                            }}>
                                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h5" color="primary" fontWeight="bold">
                                            Recent Products
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={()=>goToProducts(userFilteredProducts)}
                                            size="small"
                                            disabled={userFilteredProducts.length === 0}
                                        >
                                            View All {userFilteredProducts.length > 0 && `(${userFilteredProducts.length})`}
                                        </Button>
                                    </Box>
                                    <Divider sx={{ mb: 3 }} />
                                    <Box sx={{ flex: 1 }}>
                                        {userFilteredProducts.slice(0, 2).map((product, index) => 
                                            renderProductCard(product, index)
                                        )}
                                        {userFilteredProducts.length === 0 && (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <ShoppingBagIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No Products Yet
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Start by uploading your first product
                                            </Typography>
                                            <Button variant="contained" onClick={() => navigate('/product-submit')}>
                                                Submit Product
                                            </Button>
                                        </Box>
                                    )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Fade>

                        {/* Projects Section */}
                        <Fade in={true}>
                            <Card sx={{ 
                                borderRadius: 3,
                                flex: 1,
                                minHeight: { xs: 200, md: 240 }
                            }}>
                                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h5" color="primary" fontWeight="bold">
                                            Recent Projects
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={()=>goToProjects(userFilteredProjects)}
                                            size="small"
                                            disabled={userFilteredProjects.length === 0}
                                        >
                                            View All {userFilteredProjects.length > 0 && `(${userFilteredProjects.length})`}
                                        </Button>
                                    </Box>
                                    <Divider sx={{ mb: 3 }} />

                                    <Box sx={{ flex: 1 }}>
                                        {userFilteredProjects.slice(0, 2).map((project, index) => 
                                            renderProjectCard(project, index)
                                        )}

                                        {userFilteredProjects.length === 0 && (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No Projects Yet
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Start by uploading your first project
                                            </Typography>
                                            <Button variant="contained" onClick={() => navigate('/project-submit')}>
                                                Submit Project
                                            </Button>
                                        </Box>
                                    )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Fade>
                    </Stack>
                </Grid>
                
                {/* Right column - Can be used for additional content */}
                <Grid item xs={12} md={6}>
                    {/* Additional content can be added here */}
                </Grid>
            </Grid>
        </Box>
    </Container>
    </>
);
};

export default Dashboard;
